import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaIndustry,
  FaCog,
  FaSyncAlt,
  FaSearch,
  FaCheck,
  FaUserPlus,
} from 'react-icons/fa';
import { crmIndiaMartLeadApi } from '../../api/crmApi';
import AppDropdown from '../../components/AppDropdown';
import './CRM.css';
import './CRM_INDIAMART_LEADS.css';

const fallbackLeads = [
  { id: 1, indiamartLeadId: 'IM-8821', customerName: 'Vikram Singh', company: 'Singh Welders', city: 'Delhi', mobile: '9821112233', email: 'vikram@singhwelders.in', requirement: 'Oxygen Cylinders x 30', product: 'Oxygen Cylinder', status: 'New', receivedAt: '2026-10-03T09:15:00', synced: true, converted: false },
  { id: 2, indiamartLeadId: 'IM-8820', customerName: 'Fatima Sheikh', company: 'Sheikh Gas Center', city: 'Mumbai', mobile: '9820001122', email: 'fatima@sheikhgas.in', requirement: 'LPG Cylinders x 100', product: 'LPG Cylinder', status: 'Contacted', receivedAt: '2026-10-02T16:40:00', synced: true, converted: false },
  { id: 3, indiamartLeadId: 'IM-8819', customerName: 'Prakash Joshi', company: 'Joshi Engineering', city: 'Pune', mobile: '9822334455', email: 'prakash@joshiengg.in', requirement: 'Acetylene Cylinders x 20', product: 'Acetylene Cylinder', status: 'Converted', receivedAt: '2026-10-01T11:20:00', synced: true, converted: true },
  { id: 4, indiamartLeadId: 'IM-8818', customerName: 'Ramesh Patil', company: 'Patil Steel Works', city: 'Nashik', mobile: '9823445566', email: '', requirement: 'Industrial gas trolley + cylinders', product: 'Other', status: 'New', receivedAt: '2026-09-30T14:05:00', synced: false, converted: false },
];

const fmtTime = (d) => d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

const SkeletonBlock = ({ width = '100%', height = 16, radius = 8, style = {} }) => (
  <span className="crm-skeleton" style={{ width, height, borderRadius: radius, ...style }} />
);

const STATUS_OPTIONS = ['New', 'Contacted', 'Qualified', 'Converted']
  .map((s) => ({ value: s, label: s }));

export default function CRMIndiaMartLeads() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState(fallbackLeads);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const syncRef = useRef(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await crmIndiaMartLeadApi.getAll({ size: 100 });
      if (Array.isArray(res.data)) setLeads(res.data);
      else if (res.data?.content) setLeads(res.data.content);
    } catch { /* fallback */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleSync = async () => {
    if (syncRef.current) return;
    syncRef.current = true;
    setSyncing(true);
    try {
      await crmIndiaMartLeadApi.sync();
      toast.success('IndiaMART sync completed.');
      fetchAll();
    } catch {
      toast.error('Sync failed. Check IndiaMART settings.');
    } finally {
      syncRef.current = false;
      setSyncing(false);
    }
  };

  const handleConvert = async (lead) => {
    if (!window.confirm(`Convert IndiaMART lead "${lead.customerName}" into a CRM Lead?`)) return;
    try {
      await crmIndiaMartLeadApi.getById(lead.id);
      toast.success('Lead converted. Opening new lead form...');
      navigate('/crm/leads/new', { state: { fromIndiaMart: true, indiaMartLead: lead } });
    } catch {
      navigate('/crm/leads/new', { state: { fromIndiaMart: true, indiaMartLead: lead } });
    }
  };

  const filtered = leads.filter((l) => {
    if (statusFilter && l.status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (l.customerName || '').toLowerCase().includes(q) || (l.company || '').toLowerCase().includes(q) ||
        (l.indiamartLeadId || '').toLowerCase().includes(q) || (l.mobile || '').includes(q);
    }
    return true;
  });

  const counts = {
    total: leads.length,
    new: leads.filter((l) => l.status === 'New').length,
    converted: leads.filter((l) => l.converted || l.status === 'Converted').length,
    pending: leads.filter((l) => !l.synced).length,
  };

  return (
    <div className="crm-page crm-indiamart-leads-page">
      <div className="crm-header">
        <div className="crm-header-left">
          <div className="crm-page-icon" aria-hidden="true"><FaIndustry /></div>
          <div>
            <h1 className="crm-title">IndiaMART Leads</h1>
            <p className="crm-subtitle">Synced enquiries from IndiaMART — convert into CRM leads</p>
          </div>
        </div>
        <div className="crm-header-actions">
          <button type="button" className="crm-btn crm-btn-ghost" onClick={() => navigate('/crm/my-leads')}>
            <FaCog aria-hidden="true" /> Settings
          </button>
          <button type="button" className="crm-btn crm-btn-primary" onClick={handleSync} disabled={syncing}>
            <FaSyncAlt className={syncing ? 'crm-spin' : ''} aria-hidden="true" /> {syncing ? 'Syncing...' : 'Sync Now'}
          </button>
        </div>
      </div>

      <div className="crm-kpi-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
        {[
          { label: 'Total Leads', value: counts.total, color: '#6366F1' },
          { label: 'New', value: counts.new, color: '#0EA5E9' },
          { label: 'Converted', value: counts.converted, color: '#16A34A' },
          { label: 'Pending Sync', value: counts.pending, color: '#F59E0B' },
        ].map((k) => (
          <div className="crm-kpi-card" key={k.label}>
            {loading ? (
              <div style={{ width: '100%' }}>
                <SkeletonBlock width="50%" height={20} style={{ marginBottom: 8 }} />
                <SkeletonBlock width="75%" height={12} radius={6} />
              </div>
            ) : (
              <div>
                <div className="crm-kpi-value" style={{ color: k.color }}>{k.value}</div>
                <div className="crm-kpi-label">{k.label}</div>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="crm-filters">
        <div className="crm-search-box">
          <FaSearch className="crm-search-icon" aria-hidden="true" />
          <input
            type="text"
            aria-label="Search IndiaMART leads"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search IndiaMART leads..."
            autoComplete="off"
            spellCheck="false"
          />
        </div>
        <div className="crm-status-filter" role="group" aria-label="Filter leads by status">
          <AppDropdown
            value={statusFilter}
            options={STATUS_OPTIONS}
            onChange={setStatusFilter}
            placeholder="All Status"
            menuWidth={220}
            menuClassName="crm-dropdown-window"
          />
        </div>
        <button type="button" className="crm-btn crm-btn-sm crm-btn-ghost" onClick={() => { setSearch(''); setStatusFilter(''); }}>Clear</button>
      </div>

      <div className="crm-table-wrap">
        <table className="crm-table">
          <thead><tr><th>IndiaMART ID</th><th>Customer</th><th>Company</th><th>City</th><th>Mobile</th><th>Requirement</th><th>Received</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={i}>
                  {Array.from({ length: 9 }).map((__, j) => (
                    <td key={j}><SkeletonBlock width={j === 0 ? 70 : '85%'} height={16} radius={6} /></td>
                  ))}
                </tr>
              ))
            ) : filtered.map((l) => (
              <tr key={l.id}>
                <td><span className="crm-badge crm-badge-im">{l.indiamartLeadId}</span></td>
                <td><strong>{l.customerName}</strong></td>
                <td>{l.company}</td>
                <td>{l.city}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{l.mobile}</td>
                <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.requirement}</td>
                <td style={{ whiteSpace: 'nowrap', fontSize: '0.78rem' }}>{fmtTime(l.receivedAt)}</td>
                <td>
                  <span className={`crm-badge ${l.status === 'Converted' ? 'crm-badge-won' : l.status === 'New' ? 'crm-badge-new' : 'crm-badge-contacted'}`}>{l.status}</span>
                </td>
                <td>
                  <div className="crm-actions">
                    {!l.converted && (
                      <button type="button" className="crm-btn crm-btn-sm crm-btn-primary" onClick={() => handleConvert(l)}>
                        <FaUserPlus aria-hidden="true" /> Convert to Lead
                      </button>
                    )}
                    {l.converted && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.76rem', color: 'var(--success)', fontWeight: 600 }}>
                        <FaCheck aria-hidden="true" /> Converted
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {!loading && !filtered.length && (
              <tr><td colSpan={9}>
                <div className="crm-empty">
                  <div className="crm-empty-icon" aria-hidden="true"><FaIndustry /></div>
                  <div className="crm-empty-title">No IndiaMART leads</div>
                  <div className="crm-empty-desc">Click "Sync Now" to fetch the latest enquiries.</div>
                </div>
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
